import { D } from './money.js';
/**
 * Canonical order. Piece-rate first because it is the plant's default — a worker
 * with no engagement at all is piece-rate — so `Piece-rate + Casual` reads as
 * "the usual thing, plus casual", which is how the plant describes it.
 *
 * Order matters beyond aesthetics: these arrays are compared for equality in
 * `reporting/causes.ts` to decide whether a worker's schemes CHANGED between
 * periods, and two orderings of the same set would report a false change.
 */
export const PAY_SCHEME_ORDER = ['PIECE_RATE', 'CASUAL_TIME'];
export const PAY_SCHEME_LABEL = {
    PIECE_RATE: 'Piece-rate',
    CASUAL_TIME: 'Casual',
};
/** Shown where a worker holds no engagement in either scheme. */
export const NO_PAY_SCHEME_LABEL = '—';
/** Dedupe and put into `PAY_SCHEME_ORDER`, so equality comparisons are safe. */
export function sortPaySchemes(schemes) {
    return PAY_SCHEME_ORDER.filter((s) => schemes.includes(s));
}
/** `Piece-rate` · `Casual` · `Piece-rate + Casual` · `—`. */
export function paySchemesLabel(schemes) {
    const sorted = sortPaySchemes(schemes);
    if (sorted.length === 0)
        return NO_PAY_SCHEME_LABEL;
    return sorted.map((s) => PAY_SCHEME_LABEL[s]).join(' + ');
}
export function samePaySchemes(a, b) {
    const [x, y] = [sortPaySchemes(a), sortPaySchemes(b)];
    return x.length === y.length && x.every((s, i) => s === y[i]);
}
/**
 * The ONE scheme a single-valued field must carry — `payroll_line.pay_scheme`,
 * and through it `statutory_rule.applies_pay_scheme`.
 *
 * **[D] `CASUAL_TIME` only when it is the sole scheme held; otherwise
 * `PIECE_RATE`.** This extends the pre-existing fallback in
 * `payroll.prisma-repository.ts` ("no engagement ⇒ `PIECE_RATE`, the plant's
 * default scheme") rather than replacing it, so no payroll line that computed
 * before migration 0016 changes value.
 *
 * It is deliberately NOT "whichever scheme earned more". That would make a
 * statutory applicability predicate depend on the month's money, so re-running a
 * period after a late job line could move a worker onto a different rule — the
 * opposite of the reproducibility `result_hash` exists to guarantee.
 */
export function primaryPayScheme(schemes) {
    return schemes.length === 1 && schemes[0] === 'CASUAL_TIME' ? 'CASUAL_TIME' : 'PIECE_RATE';
}
/**
 * The schemes a worker was actually PAID under in one period, read off the
 * `payroll_line` row rather than off any engagement.
 *
 * Held terms and paid work are different facts and both are worth showing. A
 * worker can hold casual terms all year and book no hours in March; the Worker
 * Register should still say `Piece-rate + Casual` (those are their terms) while
 * March's payslip says `Piece-rate` (that is what they were paid).
 *
 * ── Piece is detected by COUNT first, money second ──────────────────────────
 * **[D] The counters are the primary signal and the money is the fallback**, not
 * the other way round. BR-3 penalty lines are negative earnings (`PN-900`,
 * "Broken Bag"), so a month's piece columns can net to exactly zero — a RM 16.48
 * share against a RM 16.48 penalty — while the worker plainly did piece work. On
 * money alone that worker's payslip would name no scheme at all, and
 * reconciliation would report a scheme change that never happened.
 *
 * `jobParticipationCount` / `jobsLedCount` are the same two facts the removed
 * `W_SCHEME_MISMATCH` used to test, and they cannot cancel.
 *
 * The money terms are still needed: an OT pot share carries no participation row,
 * and they are the union of all three piece-side columns because a leader who led
 * every job and took no ordinary share is still a piece-rate earner. `!== 0`,
 * not `> 0`, so a net-negative piece month still counts as piece work.
 *
 * Casual needs no counter: `category_pay` is `(minutes ÷ 60) × multiplier ×
 * hourly_rate` (R-6) with all three terms non-negative, so `casualTimeTotal`
 * cannot cancel itself to zero the way the piece side can.
 */
export function paySchemesPaid(line) {
    const nonZero = (s) => !D(s).isZero();
    const schemes = [];
    if ((line.jobParticipationCount ?? 0) > 0 ||
        (line.jobsLedCount ?? 0) > 0 ||
        nonZero(line.pieceRateEarnings) ||
        nonZero(line.leaderBonusTotal) ||
        nonZero(line.otShareTotal)) {
        schemes.push('PIECE_RATE');
    }
    if (nonZero(line.casualTimeTotal))
        schemes.push('CASUAL_TIME');
    return schemes;
}
//# sourceMappingURL=pay-scheme.js.map